import React, { useEffect, useMemo, useState } from 'react';
import {
  App,
  Button,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tooltip,
  Typography
} from 'antd';
import type { TableColumnsType } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/currency';
import { ORDER_CURRENCIES } from '../../modules/orders/currencies';
import {
  SUPPLIER_ORDER_MANUAL_LANDED_COST_CATEGORIES,
  supplierOrderAmountRule
} from '../../dto/supplierOrder';
import {
  SupplierDefaultCost,
  SupplierDefaultCostPayload,
  createSupplierDefaultCost,
  deleteSupplierDefaultCost,
  fetchSupplierDefaultCosts,
  updateSupplierDefaultCost
} from '../../modules/suppliers/api/supplierDefaultCostsApi';
import { useFeedback } from '../../utils/feedback';
import { getApiErrorMessage } from '../../utils/apiError';

const { Text } = Typography;
const NOTE_MAX_LENGTH = 500;

interface SupplierDefaultCostsTabProps {
  supplierId: string;
  canEdit: boolean;
}

interface DefaultCostFormValues {
  category?: SupplierDefaultCostPayload['category'];
  amount?: number;
  currency: string;
  note?: string;
}

const SupplierDefaultCostsTab: React.FC<SupplierDefaultCostsTabProps> = ({
  supplierId,
  canEdit
}) => {
  const { t } = useTranslation(['suppliers', 'common', 'orders']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [costs, setCosts] = useState<SupplierDefaultCost[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SupplierDefaultCost | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [form] = Form.useForm<DefaultCostFormValues>();

  const categoryOptions = useMemo(
    () =>
      SUPPLIER_ORDER_MANUAL_LANDED_COST_CATEGORIES.map((category) => ({
        value: category,
        label: t(`orders:supplierOrders.landedCostCategory.${category}`)
      })),
    [t]
  );

  const loadCosts = async () => {
    setLoading(true);
    try {
      setCosts(await fetchSupplierDefaultCosts(supplierId));
    } catch (error) {
      feedback.error(t('suppliers:defaultCosts.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ currency: 'PLN' });
    setModalOpen(true);
  };

  const openEdit = (cost: SupplierDefaultCost) => {
    setEditing(cost);
    form.setFieldsValue({
      category: cost.category,
      amount: cost.amount,
      currency: cost.currency,
      note: cost.note || ''
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditing(null);
  };

  const handleSubmit = async (values: DefaultCostFormValues) => {
    setSaving(true);
    const categoryLabel = t(`orders:supplierOrders.landedCostCategory.${values.category}`);
    try {
      const payload: SupplierDefaultCostPayload = {
        category: values.category as SupplierDefaultCostPayload['category'],
        amount: Number(values.amount),
        currency: values.currency,
        note: values.note || null
      };
      const amountLabel = formatCurrency(payload.amount, payload.currency);

      if (editing) {
        await updateSupplierDefaultCost(supplierId, editing.id, payload);
        feedback.success(t('suppliers:defaultCosts.updateSuccess', { category: categoryLabel, amount: amountLabel }));
      } else {
        await createSupplierDefaultCost(supplierId, payload);
        feedback.success(t('suppliers:defaultCosts.createSuccess', { category: categoryLabel, amount: amountLabel }));
      }
      setModalOpen(false);
      setEditing(null);
      await loadCosts();
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'suppliers:defaultCosts.saveFailed', undefined, {
        category: categoryLabel,
      }));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (cost: SupplierDefaultCost) => {
    const categoryLabel = t(`orders:supplierOrders.landedCostCategory.${cost.category}`);
    modal.confirm({
      title: t('suppliers:defaultCosts.deleteConfirmTitle'),
      content: t('suppliers:defaultCosts.deleteConfirmDescription'),
      okText: t('common:yes'),
      cancelText: t('common:no'),
      okButtonProps: { danger: true },
      onOk: async () => {
        setDeletingId(cost.id);
        try {
          await deleteSupplierDefaultCost(supplierId, cost.id);
          feedback.success(t('suppliers:defaultCosts.deleteSuccess', {
            category: categoryLabel,
            amount: formatCurrency(cost.amount, cost.currency),
          }));
          await loadCosts();
        } catch (error) {
          feedback.error(getApiErrorMessage(error, t, 'suppliers:defaultCosts.deleteFailed', undefined, {
            category: categoryLabel,
          }));
        } finally {
          setDeletingId(null);
        }
      }
    });
  };

  const columns: TableColumnsType<SupplierDefaultCost> = [
    {
      title: t('suppliers:defaultCosts.category'),
      key: 'category',
      render: (_value, record) => t(`orders:supplierOrders.landedCostCategory.${record.category}`)
    },
    {
      title: t('suppliers:defaultCosts.amount'),
      key: 'amount',
      align: 'right',
      width: 200,
      render: (_value, record) => (
        <Text strong>{formatCurrency(record.amount, record.currency)}</Text>
      )
    },
    {
      title: t('suppliers:defaultCosts.note'),
      dataIndex: 'note',
      key: 'note',
      render: (value?: string | null) => value || <Text type="secondary">—</Text>
    }
  ];

  if (canEdit) {
    columns.push({
      title: '',
      key: 'actions',
      width: 110,
      align: 'right',
      render: (_value, record) => (
        <Space>
          <Tooltip title={t('common:edit')}>
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          <Tooltip title={t('common:delete')}>
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              loading={deletingId === record.id}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      )
    });
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 16 }}>
        <Text type="secondary">{t('suppliers:defaultCosts.description')}</Text>
        {canEdit && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            {t('suppliers:defaultCosts.addCost')}
          </Button>
        )}
      </div>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        {t('suppliers:defaultCosts.hint')}
      </Text>

      <Table<SupplierDefaultCost>
        columns={columns}
        dataSource={costs}
        rowKey="id"
        loading={loading}
        pagination={false}
        locale={{ emptyText: <Empty description={t('suppliers:defaultCosts.empty')} /> }}
      />

      <Modal
        title={editing ? t('suppliers:defaultCosts.editCost') : t('suppliers:defaultCosts.addCost')}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => form.submit()}
        okText={t('common:save')}
        cancelText={t('common:cancel')}
        confirmLoading={saving}
        maskClosable={false}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} preserve={false}>
          <Form.Item
            name="category"
            label={t('suppliers:defaultCosts.category')}
            rules={[{ required: true, message: t('suppliers:defaultCosts.categoryRequired') }]}
          >
            <Select options={categoryOptions} placeholder={t('suppliers:defaultCosts.selectCategory')} />
          </Form.Item>

          <Space.Compact style={{ width: '100%' }}>
            <Form.Item
              name="amount"
              label={t('suppliers:defaultCosts.amount')}
              style={{ flex: 1, marginRight: 8 }}
              rules={[{ ...supplierOrderAmountRule(t), required: true }]}
            >
              <InputNumber
                step={0.01}
                precision={2}
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item name="currency" label={t('suppliers:defaultCosts.currency')} style={{ width: 120 }}>
              <Select>
                {ORDER_CURRENCIES.map((option) => (
                  <Select.Option key={option} value={option}>
                    {option}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Space.Compact>

          <Form.Item name="note" label={t('suppliers:defaultCosts.note')}>
            <Input.TextArea rows={2} maxLength={NOTE_MAX_LENGTH} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SupplierDefaultCostsTab;
